import { Link } from "react-router-dom";
import {
  BadgePoundSterling,
  ShieldCheck,
  Users,
  CreditCard,
  Banknote,
  Ban,
  CalendarCheck,
  MapPin,
  ArrowRight,
  Car,
  Route,
} from "lucide-react";
import Seo from "../components/Seo";
import DisclaimerBand from "../components/DisclaimerBand";
import { SITE, IMAGES } from "../config/site";

const passengerBenefits = [
  { icon: BadgePoundSterling, text: "£100 fixed fare for confirmed journeys" },
  { icon: ShieldCheck, text: "TfL-licensed London Black Cab drivers" },
  { icon: Car, text: "Comfortable, spacious vehicles" },
  { icon: Users, text: "Professional drivers" },
  { icon: CreditCard, text: "Cash or card payment inside the taxi" },
  { icon: Ban, text: "No advance payment" },
  { icon: Ban, text: "No surge pricing" },
  { icon: CalendarCheck, text: "Pre-booked journey requests" },
  { icon: Route, text: "Excellent value on suitable long-distance trips" },
];

export default function Home() {
  return (
    <main data-testid="home-page">
      <Seo
        title="Essex ↔ London Black Cab — £100 Fixed Fare"
        description="Pre-book a £100 fixed-fare London Black Cab between Great Dunmow, Felsted, Bishop's Stortford, Stansted Mountfitchet, Takeley and London. Pay your driver in the cab."
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#27272A]">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src={IMAGES.hero.url}
            alt={IMAGES.hero.alt}
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:py-32">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">
            {SITE.tagline}
          </p>
          <h1 className="mt-5 text-4xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl">
            ESSEX <span className="text-[#D4AF37]">↔</span> LONDON
            <span className="mt-3 block text-[#D4AF37]">£100 FIXED FARE</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#D4D4D8] sm:text-lg">
            Travelling between Essex and London? Enjoy a professional London Black Cab journey
            for just £100 when one of our licensed Essex-based drivers is already heading your
            way.
          </p>
          <div className="mt-9 flex flex-col gap-4 sm:flex-row">
            <Link
              to="/book"
              data-testid="hero-request-journey-cta"
              className="focus-gold inline-flex items-center justify-center gap-2 border border-[#D4AF37] bg-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158]"
            >
              Request a £100 Journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link
              to="/drivers"
              data-testid="hero-join-driver-cta"
              className="focus-gold inline-flex items-center justify-center gap-2 border border-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-[#D4AF37] transition-colors duration-150 hover:bg-[#D4AF37] hover:text-black"
            >
              Join as a Driver
            </Link>
          </div>
        </div>
      </section>

      {/* PAYMENT STRIP */}
      <section className="border-b border-[#27272A] bg-[#111111]">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 py-10 sm:grid-cols-3 sm:px-6">
          {[
            { icon: Banknote, title: "Pay in the cab", text: "£100 by cash or card, direct to your driver at the end of the journey." },
            { icon: Ban, title: "No advance payment", text: "This website never takes payment. No deposits, no card details, no hidden charges." },
            { icon: ShieldCheck, title: "Licensed & professional", text: "TfL-licensed London Black Cab drivers who live in Essex." },
          ].map((item) => (
            <div key={item.title} className="flex items-start gap-4" data-testid={`payment-strip-${item.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <item.icon className="mt-1 h-7 w-7 shrink-0 text-[#D4AF37]" aria-hidden="true" />
              <div>
                <h2 className="text-lg font-semibold text-white">{item.title}</h2>
                <p className="mt-1 text-sm leading-relaxed text-[#A1A1AA]">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS TEASER */}
      <section className="section-pad mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">How it works</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Drivers already travelling your way
          </h2>
          <p className="mt-4 leading-relaxed text-[#A1A1AA]">
            Many Essex-based London Black Cab drivers travel into London each morning and return
            to Essex each evening — often with an empty cab. We match your journey request with a
            driver already heading in your direction, so you get a fixed £100 fare and the driver
            earns instead of driving empty.
          </p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            { step: "01", title: "Request your journey", text: "Tell us your direction, pickup, destination, date and time. Your fixed fare is always £100 — there is nothing to calculate." },
            { step: "02", title: "We check driver availability", text: "We personally review your request and check it against our Essex-based drivers' existing travel plans, then contact you to confirm." },
            { step: "03", title: "Pay £100 in the cab", text: "Travel in a licensed London Black Cab and pay your driver £100 by cash or card at the end of the journey. No advance payment, ever." },
          ].map((s) => (
            <div
              key={s.step}
              data-testid={`step-${s.step}`}
              className="border border-[#27272A] bg-[#111111] p-8 transition-colors duration-150 hover:border-[rgba(212,175,55,0.4)]"
            >
              <p className="font-serif text-4xl font-bold text-[#D4AF37]">{s.step}</p>
              <h3 className="mt-4 text-xl font-semibold text-white">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA]">{s.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <Link
            to="/how-it-works"
            data-testid="home-how-it-works-link"
            className="focus-gold inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[#D4AF37] hover:text-[#E5C158]"
          >
            Read how it works in full <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      {/* BENEFITS — PASSENGERS & DRIVERS */}
      <section className="border-y border-[#27272A] bg-[#0B0B0B]">
        <div className="section-pad mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">For passengers</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white">
                A proper London Black Cab, one fixed fare
              </h2>
              <ul className="mt-8 space-y-4">
                {passengerBenefits.map((b) => (
                  <li key={b.text} className="flex items-start gap-3 text-[#D4D4D8]">
                    <b.icon className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                    <span className="text-sm sm:text-base">{b.text}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/book"
                data-testid="passenger-benefits-cta"
                className="focus-gold mt-8 inline-flex items-center gap-2 border border-[#D4AF37] bg-[#D4AF37] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158]"
              >
                Request a £100 Journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="border-[#27272A] lg:border-l lg:pl-12">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">For drivers</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white">
                Essex Black Cab driver?
              </h2>
              <p className="mt-4 font-serif text-2xl font-semibold uppercase tracking-wide text-[#D4AF37]">
                Join our network
              </p>
              <p className="mt-4 max-w-md leading-relaxed text-[#A1A1AA]">
                Turn your empty journeys between Essex and London into paid work. Accept only
                bookings that suit your existing travel plans.
              </p>
              <Link
                to="/drivers"
                data-testid="driver-benefits-cta"
                className="focus-gold mt-8 inline-flex items-center gap-2 border border-[#D4AF37] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-[#D4AF37] transition-colors duration-150 hover:bg-[#D4AF37] hover:text-black"
              >
                Join as a Driver <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AREAS TEASER */}
      <section className="section-pad mx-auto max-w-7xl px-4 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Areas covered</p>
        <h2 className="mt-3 max-w-2xl text-3xl font-semibold tracking-tight text-white sm:text-4xl">
          The Essex ↔ London M11 corridor
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <Link to="/areas-covered" data-testid="areas-card-essex" className="group focus-gold relative block overflow-hidden border border-[#27272A]">
            <img src={IMAGES.essex.url} alt={IMAGES.essex.alt} className="h-72 w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <h3 className="text-2xl font-semibold text-white">From Essex</h3>
              <p className="mt-1 text-sm text-[#D4D4D8]">
                Great Dunmow, Felsted, Bishop's Stortford, Stansted, Sawbridgeworth, Harlow &amp; M11 villages
              </p>
            </div>
          </Link>
          <Link to="/areas-covered" data-testid="areas-card-london" className="group focus-gold relative block overflow-hidden border border-[#27272A]">
            <img src={IMAGES.london.url} alt={IMAGES.london.alt} className="h-72 w-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute bottom-0 p-6">
              <h3 className="text-2xl font-semibold text-white">To London</h3>
              <p className="mt-1 text-sm text-[#D4D4D8]">
                City of London, Liverpool Street, Bank, Canary Wharf &amp; Central London
              </p>
            </div>
          </Link>
        </div>
        <div className="mt-10 flex items-start gap-3">
          <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
          <p className="text-sm leading-relaxed text-[#A1A1AA]">
            We operate in two directions only — Essex → London (mainly mornings to late
            afternoon) and London → Essex (mainly evenings to late night). Every request is
            considered individually; drivers decide which journeys suit their plans.
          </p>
        </div>

        {/* POPULAR ROUTES — internal links */}
        <div className="mt-14">
          <h3 className="text-xl font-semibold text-white">Popular routes</h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#A1A1AA]">
            Our most requested journeys, each with local pickup details and return-journey
            information.
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                to: "/great-dunmow-to-london",
                testid: "home-route-dunmow",
                title: "Great Dunmow to London",
                text: "Pickups in Great Dunmow, Flitch Green and the CM6 villages, into the City and Central London.",
              },
              {
                to: "/felsted-flitch-green-to-london",
                testid: "home-route-felsted",
                title: "Felsted & Flitch Green to London",
                text: "From Felsted, Flitch Green and the villages between Dunmow and Chelmsford to London.",
              },
              {
                to: "/bishops-stortford-to-london",
                testid: "home-route-stortford",
                title: "Bishop's Stortford to London",
                text: "M11 corridor pickups in and around Bishop's Stortford, into the City and Central London.",
              },
              {
                to: "/stansted-mountfitchet-takeley-to-london",
                testid: "home-route-stansted",
                title: "Stansted Mountfitchet & Takeley to London",
                text: "Village pickups in Stansted Mountfitchet and Takeley — a village-to-London service, not an airport transfer.",
              },
            ].map((r) => (
              <Link
                key={r.to}
                to={r.to}
                data-testid={r.testid}
                className="focus-gold group flex items-start justify-between gap-4 border border-[#27272A] bg-[#111111] p-6 transition-colors duration-150 hover:border-[rgba(212,175,55,0.4)]"
              >
                <div>
                  <p className="font-semibold text-white group-hover:text-[#D4AF37]">{r.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-[#A1A1AA]">{r.text}</p>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DISCLAIMER + CTA */}
      <section className="border-t border-[#27272A] bg-[#111111]">
        <div className="section-pad mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Ready to travel for <span className="text-[#D4AF37]">£100</span>?
          </h2>
          <p className="mx-auto mt-4 max-w-xl leading-relaxed text-[#A1A1AA]">
            Send us your journey request and we will check whether one of our Essex-based London
            Black Cab drivers is already travelling your way.
          </p>
          <Link
            to="/book"
            data-testid="home-bottom-cta"
            className="focus-gold mt-8 inline-flex items-center gap-2 border border-[#D4AF37] bg-[#D4AF37] px-8 py-4 text-sm font-semibold uppercase tracking-wider text-black transition-colors duration-150 hover:bg-[#E5C158]"
          >
            Request a £100 Journey <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <div className="mx-auto mt-10 max-w-2xl text-left">
            <DisclaimerBand />
          </div>
        </div>
      </section>
    </main>
  );
}
